/**
 * useDiscoverySummary Hook
 * Fetches 7-day summary statistics for discovery operations
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-J: route the 7-day discovery summary lookback through the
// canonical clock so query bounds are deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

interface DiscoverySummary {
  newPending: number;
  promoted: number;
  duplicates: number;
  rejected: number;
  lastRun: {
    timestamp: string;
    status: string;
    error: string | null;
    triggerReason: string | null;
  } | null;
}

export function useDiscoverySummary() {
  return useQuery({
    queryKey: ['discovery-summary', '7d'],
    queryFn: async (): Promise<DiscoverySummary> => {
      const sevenDaysAgo = new Date(clock.nowMs() - 7 * 24 * 60 * 60 * 1000).toISOString();
      
      // Get counts from discovered_training_artifacts using existing columns
      const { data: artifacts, error: artifactsError } = await supabase
        .from('discovered_training_artifacts')
        .select('is_promoted, is_rejected, created_at, promoted_at, rejected_at, status, duplicate_of_artifact_id')
        .gte('created_at', sevenDaysAgo);
      
      if (artifactsError) {
        console.error('[useDiscoverySummary] Error fetching artifacts:', artifactsError);
        throw artifactsError;
      }
      
      // Count by status - use status column if available, fallback to boolean flags
      const newPending = (artifacts || []).filter(
        (a: any) => {
          const isPending = a.status === 'pending' || (!a.is_promoted && !a.is_rejected && !a.duplicate_of_artifact_id);
          return isPending && new Date(a.created_at) >= new Date(sevenDaysAgo);
        }
      ).length;
      
      const promoted = (artifacts || []).filter(
        (a: any) => {
          const isPromoted = a.status === 'promoted' || a.is_promoted;
          return isPromoted && a.promoted_at && new Date(a.promoted_at) >= new Date(sevenDaysAgo);
        }
      ).length;
      
      const duplicates = (artifacts || []).filter(
        (a: any) => {
          return a.status === 'duplicate' || a.duplicate_of_artifact_id;
        }
      ).length;
      
      const rejected = (artifacts || []).filter(
        (a: any) => {
          const isRejected = a.status === 'rejected' || a.is_rejected;
          return isRejected && a.rejected_at && new Date(a.rejected_at) >= new Date(sevenDaysAgo);
        }
      ).length;
      
      // Get last fetch run from discovery_logs
      const { data: lastRunData } = await supabase
        .from('discovery_logs')
        .select('created_at, status, error_message, trigger_reason')
        .eq('operation', 'fetch')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      let lastRun: DiscoverySummary['lastRun'] = null;
      if (lastRunData) {
        lastRun = {
          timestamp: lastRunData.created_at,
          status: lastRunData.status,
          error: lastRunData.error_message,
          triggerReason: lastRunData.trigger_reason,
        };
      }
      
      return {
        newPending,
        promoted,
        duplicates,
        rejected,
        lastRun,
      };
    },
    refetchInterval: 60000,
  });
}
